import { Popover, Tooltip } from "@mui/material";
import { ArrowLeft, Eye, Filter } from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  CaretDownFill,
  CaretUpFill,
  PlusCircle,
  QuestionCircleFill,
  StarFill,
} from "react-bootstrap-icons";
import API from "../../API/newIndex";
import { DataLoad, PlanPermission } from "../../App";
import { TableColumnsContext } from "../../controllers/TableColumnsContext";
import { useUser } from "../../controllers/UserContext";
import { formatDate } from "../../controllers/FormatAmount";
import { defaultReviewsField } from "../../utilities/defaultCustomTableField";
import { NOPHOTO_IMAGE } from "../../Assets/StaticData/StaticImage";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { getInitialPage, replacePageInUrl } from "../../controllers/PageHandleFromQuery";
import { parentNavigate } from "../../controllers/ParentNavigate";
import Notification from "../Notification";
import {
  ButtonComponent,
  CommonTable,
  PaginationComponent,
  PageLimitDropDownComponent,
  SearchComponent,
  SelectComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import { isAscendingOrder } from "../../utilities/staticFunction";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import useTableApi from "../../utilities/useTableApi";

const stripHtml = (html) => {
  if (typeof html !== "string") return html;
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
};

function AllReviews({ permissionCheck }) {
  const handleTableApi = useTableApi();
  const { tableColumnsData } = useContext(TableColumnsContext);
  const [allReviews, setReviews] = useState();
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const [id, setId] = useState("");
  const { setPlanPermission } = useContext(PlanPermission);
  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.reviews || sortData?.review || sortData?.allReviews;

  const [Page, setPage] = useState(getInitialPage());
  const initialLimit = localStorage.getItem("limit") || "10";
  const [limit, setLimit] = useState(initialLimit);
  const [sortBy, setSortBy] = useState(() => savedSort?.sort_by || "createdAt");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch((prev) => {
        if (prev !== searchQuery) {
          setPage(1);
        }
        return searchQuery;
      });
    }, 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [autocompleteSearch, setAutocompleteSearch] = useState();
  const searchTimer = useRef(null);

  const [orderBy, setOrderBy] = useState(() => {
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isSortSynced = useRef(false);

  useEffect(() => {
    if (isUserLoaded && !isSortSynced.current) {
      isSortSynced.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.reviews) && dataObj.reviews.length > 0) return dataObj.reviews;
    if (Array.isArray(dataObj?.review) && dataObj.review.length > 0) return dataObj.review;
    if (Array.isArray(dataObj?.allReviews) && dataObj.allReviews.length > 0) return dataObj.allReviews;
    if (Array.isArray(tableColumnsData?.reviews) && tableColumnsData.reviews.length > 0) return tableColumnsData.reviews;
    if (Array.isArray(tableColumnsData?.review) && tableColumnsData.review.length > 0) return tableColumnsData.review;
    if (Array.isArray(tableColumnsData?.allReviews) && tableColumnsData.allReviews.length > 0) return tableColumnsData.allReviews;
    return defaultReviewsField;
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'image', 'product_name', 'average_review', 'review_count', 'action', 'actions',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
      'product_id', 'product_slug_name', 'sku_id',
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' • ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: '160px',
          minWidth: '110px',
          responsiveWidth: '100px',
          headerClassName: 'px-3 whitespace-nowrap truncate',
          cellClassName: 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === '') return '-';
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            if (
              typeof val === 'string' &&
              (fieldName.toLowerCase().includes('date') ||
                fieldName.toLowerCase().includes('createdat') ||
                fieldName.toLowerCase().includes('updatedat') ||
                fieldName.toLowerCase().endsWith('at') ||
                /^\d{4}-\d{2}-\d{2}T/.test(val))
            ) {
              return formatDate(val);
            }
            if (typeof val === 'object') return JSON.stringify(val);
            const strVal = typeof val === 'string' ? stripHtml(val) : String(val);
            return <span className="line-clamp-2 truncate">{strVal || '-'}</span>;
          },
        };
      });
  }, [apiFieldNames]);

  var startRecord = (allReviews?.data?.currentPage - 1) * (limit === "all" ? allReviews?.data?.totalRecords || 1 : limit) + 1;
  var endRecord = limit === "all" ? allReviews?.data?.totalRecords : Math.min(allReviews?.data?.currentPage * limit, allReviews?.data?.totalRecords);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    replacePageInUrl(Page);
  }, [Page]);

  // Sorting Data
  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  // All reviews
  const getAllReviews = async () => {
    setDataLoading(true);
    try {
      const searchParam = debouncedSearch.trim()
        ? `&searchkey=${encodeURIComponent(debouncedSearch.trim())}`
        : "";
      let Apicall = await API({
        url: `/admin/review/getAllReviews?page=${Page}&limit=${limit}&product=${id}&sort_by=${sortBy}&order_by=${orderBy.orderByValue}${searchParam}`,
        method: "get",
      });
      if (Apicall.status === 200 || Apicall.status === 304) {
        const result = Apicall?.data;
        setReviews(result);
        const total = Number(result?.data?.totalRecords || 0);
        const start = Number(startRecord || 0);

        if (start > total && Page > 1) {
          setPage(Page - 1);
        }
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "reviews" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  const [allProducts, setAllProducts] = useState([]);
  // product
  const getProducts = async ({ search } = {}) => {
    try {
      const params = {};
      if (typeof search === "string" && search.trim().length > 0) {
        params.searchkey = search.trim();
      }
      const Apicall = await API({ url: `/admin/product/GetProductsName`, method: "get", params: params });
      if (Apicall.status === 200 || Apicall.status === 304) {
        const result = Apicall?.data;
        setAllProducts(result?.data);
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    }
  };

  useEffect(() => {
    if (!isUserLoaded && !isSortSynced.current) {
      return;
    }
    getAllReviews();
  }, [Page, limit, id, sortBy, orderBy, isUserLoaded, debouncedSearch]);

  useEffect(() => {
    getProducts();
  }, []);

  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const resetFilter = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFilter(false);
    setAnchorEl(null);
    setOrderBy({ orderByValue: "DESC", isDecending: true });
    setSortBy("createdAt");
    setId("");
    setSearchQuery("");
    setDebouncedSearch("");
    setAutocompleteSearch();
    setPage(getInitialPage() || 1);
    setLimit(limit === "all" ? 10 : limit);
  };

  const doesMatchExist = (text) => {
    if (!allProducts?.records?.length) return false;
    const q = text.toLowerCase();
    return allProducts.records.some((p) =>
      `${p.product_name} ${p.sku_id}`.toLowerCase().includes(q),
    );
  };

  const filterOptionsForProduct = (option, inputValue) => {
    if (!inputValue || inputValue.trim().length < 3) {
      return false; // Minimum 3 characters
    }
    const item = option.data;
    const combinedFields = `${item.product_name || item.label || ''} ${item.sku_id || ''}`.toLowerCase();
    return combinedFields.includes(inputValue.toLowerCase());
  };

  const handleInputChange = (inputValue, { action }) => {
    if (action !== "input-change") return;
    setAutocompleteSearch(inputValue);

    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }
    if (inputValue.length === 0) {
      getProducts();
      return;
    }
    if (inputValue.length >= 3) {
      const exists = doesMatchExist(inputValue);
      if (!exists) {
        searchTimer.current = setTimeout(() => {
          getProducts({ search: inputValue });
        }, 500);
      }
    }
  };

  const displayRecords = allReviews?.data?.records || [];

  // Columns definition for CommonTable
  const columns = [
    {
      key: "image",
      title: t("lang_image"),
      width: "80px",
      headerClassName: "thead-image px-3",
      cellClassName: "table-image px-3 min-w-[80px]",
      render: (elem) => (
        <img
          onError={(e) => {
            e.target.src = NOPHOTO_IMAGE;
          }}
          src={elem?.image || NOPHOTO_IMAGE}
          width={40}
          height={50}
          className="p-1 max-h-full"
          alt={elem?.product_name}
        />
      ),
    },
    {
      key: "product_name",
      width: "300px",
      maxWidth: "350px",
      title: t("lang_Product_Name") || "Product Name",
      headerClassName: "px-3 w-full",
      cellClassName: "w-full px-3",
      render: (elem) => <span className="line-clamp-2 truncate">{elem?.product_name || "-"}</span>,
    },
    {
      key: "average_review",
      title: t("lang_Rating") || "Rating",
      headerClassName: "px-3 min-w-[150px] max-w-[150px]",
      cellClassName: "px-3 min-w-[150px] max-w-[150px]",
      render: (elem) => {
        const rating = elem?.average_review;
        const bgClass =
          rating >= 5
            ? "bg-[#00b67a]"
            : rating >= 4
              ? "bg-[#73cf11]"
              : rating >= 3
                ? "bg-[#ffce00]"
                : rating >= 2
                  ? "bg-[#ff8622]"
                  : "bg-[#ff3722]";
        return rating !== undefined && rating !== null ? (
          <div
            className={`inline-flex items-center space-x-2 ${bgClass} text-white px-3 py-0.5 rounded-[6px]`}
          >
            <StarFill />
            <span className="font-medium text-sm">{rating}</span>
          </div>
        ) : null;
      },
    },
    {
      key: "review_count",
      title: t("lang_Review_Count") || "Review Count",
      align: "text-right",
      headerClassName: "min-w-[200px] max-w-[200px] px-3 text-right",
      cellClassName: "min-w-[200px] max-w-[200px] px-3 text-right",
      render: (elem) => <span className="line-clamp-2">{elem?.review_count || "-"}</span>,
    },
    ...dynamicColumns,
    {
      key: "action",
      title: <span className="text-center w-full capitalize">{t("lang_action")}</span>,
      align: "text-center",
      width: "80px",
      maxWidth: "80px",
      headerClassName: "px-3 text-center",
      cellClassName: "px-3 text-center",
      ignoreRowClick: true,
      render: (elem) => (
        <div className="w-full text-center">
          <Tooltip title="View" arrow>
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate(`/reviews/${elem?.product_id}`);
              }}
            >
              <Eye className="w-[17px] h-[17px]" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  // Options for product select
  const productSelectOptions = (allProducts?.records || []).map((prod) => ({
    value: prod._id,
    label: prod.product_name,
    product_name: prod.product_name,
    product_slug_name: prod.product_slug_name,
    image: prod.image,
    sku_id: prod.sku_id,
    _id: prod._id,
  }));

  // Options for items limit select
  const limitOptions = [
    { value: "10", label: "10" },
    { value: "20", label: "20" },
    { value: "50", label: "50" },
    { value: "all", label: t("lang_all") },
  ];

  // Card Header controls (Desktop: Search + Limit + ManageColumns, Mobile: Filter Icon Button)
  const tableHeader = (manageFieldsBtn) => (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b main-border-color bg-[var(--secondary-bg-color)] min-h-[52px]">
      {/* Left: Tab / Status pill (All 434) */}
      <div className="flex items-center gap-2 shrink-0">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold bg-[var(--primary-bg-color)] text-[var(--primary-text-color)] border border-[var(--border-color)]">
          All <span className="text-[var(--table-text-color)] font-medium">{allReviews?.data?.totalRecords || 0}</span>
        </span>
      </div>

      {/* Right: Desktop Search, Limit & Columns, Mobile Filter Icon Button */}
      <div className="flex items-center gap-2.5 ml-auto shrink-0">
        {/* Desktop View: Search, Limit & Columns */}
        <div className="hidden md:flex items-center gap-2.5">
          <div className="w-48 lg:w-64">
            <SearchComponent
              placeholder="Search Products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target ? e.target.value : (e || ""))}
              onClear={() => setSearchQuery("")}
              size="sm"
            />
          </div>

          <div className="w-20">
            <PageLimitDropDownComponent
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              totalRecords={allReviews?.data?.totalRecords || 0}
            />
          </div>
          {manageFieldsBtn}
        </div>

        {/* Mobile / Responsive View: Square Filter Icon Button */}
        <div className="block md:hidden">
          <Tooltip title="Filter Options" arrow>
            <ButtonComponent
              variant="outline"
              size="sm"
              className="!w-9 !h-9 !p-0 flex items-center justify-center rounded-lg"
              onClick={(e) => {
                setFilter(!filter);
                setAnchorEl(e.currentTarget);
              }}
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </ButtonComponent>
          </Tooltip>
        </div>
      </div>
    </div>
  );

  // Card Footer Pagination
  const tableFooter = allReviews?.data?.records?.length > 0 ? (
    <PaginationComponent
      count={allReviews?.data?.totalPages}
      page={Page}
      onChange={(e, v) => setPage(v)}
      totalRecords={allReviews?.data?.totalRecords}
      startRecord={startRecord}
      endRecord={endRecord}
      limit={limit}
      recordsLabel="Records"
    />
  ) : null;

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        {/* Top Page Header */}
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_Reviews"), path: "/reviews" },
                ]}
              />
              <div className="flex items-end">
                <h4 className="text-xl md:text-2xl font-semibold text-primary">
                  {t("lang_Reviews")}{" "}
                </h4>
                <a
                  target="blank"
                  href="https://help.shopeasy.ai/articles/review"
                  className="how-it-works"
                >
                  <QuestionCircleFill className="me-2" />
                  {t("lang_how_to_use")}
                </a>
              </div>
            </div>
          </div>
          <div className="flex space-x-3 items-center">
            {permissionCheck && (
              <ButtonComponent
                variant="primary"
                size="md"
                icon={<PlusCircle className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
                onClick={() => parentNavigate("/reviews/add")}
              >
                {t("lang_Add_Review")}
              </ButtonComponent>
            )}
          </div>
        </div>

        {/* Table Container Card */}
        <div className="flex w-full h-full overflow-hidden relative flex-col">
          <CommonTable
            tableId="reviews"
            tableHeader={tableHeader}
            tableFooter={tableFooter}
            columns={columns}
            data={displayRecords}
            loading={dataLoading && !allReviews}
            skeletonRows={10}
            onSort={SortData}
            sortBy={sortBy}
            orderBy={orderBy}
            containerClassName="w-full transition-all duration-100 border main-border-color inline-block overflow-x-auto overflow-y-hidden rounded-[8px] shadow-sm"
            NEWAPI={handleTableApi}
            tableDefaults={defaultCustomFields?.defaultReviewsField}
            tableWiseAllowedSortKeys={defaultCustomFields?.REVIEW_SORT_KEYS}
            userState={userContext}
          />

          <Popover
            open={filter}
            anchorEl={anchorEl}
            onClose={() => {
              setFilter(false);
              setAnchorEl(null);
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            PaperProps={{
              style: { marginRight: "35px", marginTop: "10px" },
            }}
            className="rounded-[6px]"
          >
            <div className="p-[20px] filter min-w-[280px]">
              <div>
                <div className="flex justify-between items-center">
                  <p className="flex font-semibold  text-sm m-0 items-center">
                    <Filter className="me-1 w-4 h-4" />
                    {t("lang_filter")}
                  </p>
                  <ButtonComponent
                    variant="secondary"
                    size="sm"
                    onClick={(e) => resetFilter(e)}
                  >
                    {t("lang_reset_filter")}
                  </ButtonComponent>
                </div>
                <hr className="main-border-color my-3" />
                <div className="grid gap-y-4">
                  <div>
                    <SelectComponent
                      label={t("lang_Sort_By_Product")}
                      placeholder="Select product"
                      options={productSelectOptions}
                      value={id}
                      onChange={(e) => {
                        setPage(1);
                        setId(e.target.value);
                      }}
                      onInputChange={handleInputChange}
                      filterOption={filterOptionsForProduct}
                      noOptionsMessage={(inputObj) =>
                        allProducts?.records?.length > 0 && !autocompleteSearch
                          ? "Please enter at least 3 characters"
                          : "No option found"
                      }
                      searchable={true}
                    />
                  </div>

                  <div>
                    <SelectComponent
                      label={t("lang_set_items_limit")}
                      options={limitOptions}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(e.target.value);
                      }}
                      searchable={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Popover>
        </div>
      </div>
    </>
  );
}

export default AllReviews;
